import { Link } from 'react-router-dom'
import {Phone, Wrench, ShieldCheck, Calendar, CheckCircle2, Sparkles, Thermometer} from 'lucide-react'
import SectionHeader from '@/components/SectionHeader'

const maintenanceImg = 'https://static.lumi.new/6f/6f3332dbdf8cf0c7a346f22adbff72ef.png'

const services = [
  {
    icon: Calendar,
    title: "Contrats d'entretien",
    desc: "Visites planifiées pour particuliers, régies et commerces. Tranquillité toute l'année.",
  },
  {
    icon: Sparkles,
    title: "Détartrage chauffe-eau",
    desc: "Prolonge la durée de vie de votre chauffe-eau et réduit votre consommation énergétique.",
  },
  {
    icon: Thermometer,
    title: "Entretien chauffage & chaudière",
    desc: "Révision annuelle de votre chaudière (gaz, mazout, électrique), purge des radiateurs et contrôle de pression.",
  },
  {
    icon: ShieldCheck,
    title: "Vérification installations",
    desc: "Contrôle complet de robinetterie, canalisations, joints, et points sensibles de fuite.",
  },
  {
    icon: Wrench,
    title: "Prévention des pannes",
    desc: "Détection précoce des risques pour éviter les pannes coûteuses et les dégâts d'eau.",
  },
]

export default function MaintenancePage() {
  return (
    <div>
      <section className="bg-navy-50 py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-navy-200 text-navy-600 text-xs font-bold uppercase tracking-wider">
              <Sparkles className="w-3.5 h-3.5 text-urgent-500" />
              Entretien & maintenance
            </div>
            <h1 className="mt-5 text-4xl lg:text-5xl font-extrabold text-navy-700 tracking-tight leading-[1.05] text-balance">
              Prévenez les pannes. Préservez vos installations.
            </h1>
            <p className="mt-5 text-lg text-navy-500 leading-relaxed">
              Nos contrats d'entretien et nos visites de contrôle vous évitent les mauvaises surprises et prolongent la durée de vie de vos équipements sanitaires.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <a href="https://fixnow.ch" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl gradient-urgent text-white font-bold shadow-orange-glow">
                <Calendar className="w-5 h-5" strokeWidth={2.5} />
                Voir notre disponibilité sur FixNow
              </a>
              <a href="tel:+41796430399" className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-white text-navy-700 font-bold border border-navy-200 hover:bg-navy-50 transition-colors">
                <Phone className="w-5 h-5" strokeWidth={2.5} />
                079 643 03 99
              </a>
            </div>
          </div>
          <div className="relative">
            <div className="rounded-3xl overflow-hidden shadow-elevated border border-navy-100">
              <img src={maintenanceImg} alt="Entretien chauffe-eau et installations sanitaires" className="w-full h-[400px] lg:h-[480px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeader
            eyebrow="Nos prestations"
            title="Entretien complet sanitaire & chauffage"
            subtitle="Des solutions sur mesure pour particuliers, régies, syndics et commerces."
          />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map((s) => (
              <div key={s.title} className="bg-white rounded-2xl p-6 border border-navy-100 hover:shadow-elevated transition-shadow">
                <div className="w-12 h-12 rounded-xl gradient-trust flex items-center justify-center mb-4">
                  <s.icon className="w-6 h-6 text-white" strokeWidth={2.2} />
                </div>
                <h3 className="text-lg font-extrabold text-navy-700">{s.title}</h3>
                <p className="mt-1.5 text-sm text-navy-500 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-navy-50">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="bg-white rounded-3xl p-8 lg:p-12 shadow-soft border border-navy-100">
            <h2 className="text-2xl lg:text-3xl font-extrabold text-navy-700 mb-6">Que comprend un contrat d'entretien ?</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                "Visite annuelle ou semestrielle planifiée",
                "Détartrage et contrôle du chauffe-eau",
                "Révision chaudière & purge radiateurs",
                "Vérification de la robinetterie",
                "Test d'étanchéité des canalisations",
                "Diagnostic complet écrit",
                "Tarif préférentiel sur les dépannages",
                "Priorité d'intervention en cas d'urgence",
              ].map((p) => (
                <div key={p} className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-5 h-5 text-urgent-500 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                  <span className="text-sm font-semibold text-navy-600">{p}</span>
                </div>
              ))}
            </div>
            <div className="mt-8 pt-6 border-t border-navy-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <div className="font-extrabold text-navy-700">Demandez votre devis personnalisé</div>
                <div className="text-sm text-navy-500">Réponse sous 24h ouvrées</div>
              </div>
              <a href="https://fixnow.ch" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl gradient-urgent text-white font-bold shadow-orange-glow">
                <Calendar className="w-5 h-5" strokeWidth={2.5} />
                Voir notre disponibilité sur FixNow
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
